import { type CSSProperties, useEffect, useMemo, useState } from 'react'
import { type Cargo, setCargo, useCargo } from '../lib/personal'
import { fmtPrice } from '../lib/rates'
import {
  type FclBasis,
  type SpotQuote,
  type WatchState,
  fclOffers,
  getLaneSpot,
  lclEstimate,
  lclLadder,
  localChargesExcluded,
} from '../lib/spot'
import { shortDate } from '../lib/workspace-rates'

// Spot rates for one lane from the spot-rate engine, FCL and LCL, read from our own database. A Fly job fetches
// them for the lanes customers watch and refreshes them daily (apps/api/src/jobs/refresh-lane-spot.ts); this
// panel never calls the engine. Styled with the board's --wr-* properties, which it inherits from LanesBoard.

const mono = "var(--font-mono, ui-monospace, 'SF Mono', Menlo, monospace)"
const label: CSSProperties = {
  fontFamily: mono,
  fontSize: 10,
  letterSpacing: '0.12em',
  textTransform: 'uppercase',
  color: 'var(--wr-muted)',
}
const th: CSSProperties = {
  ...label,
  fontWeight: 600,
  textAlign: 'left',
  padding: '9px 12px',
  borderBottom: '1px solid var(--wr-line)',
  whiteSpace: 'nowrap',
}
const td: CSSProperties = {
  padding: '10px 12px',
  borderBottom: '1px solid var(--wr-line)',
  fontSize: 14,
  color: 'var(--wr-ink)',
  verticalAlign: 'top',
}
const tab = (on: boolean): CSSProperties => ({
  font: 'inherit',
  fontSize: 13,
  fontWeight: 600,
  padding: '7px 14px',
  borderRadius: 10,
  cursor: 'pointer',
  border: '1px solid var(--wr-line)',
  background: on ? 'var(--wr-signal)' : 'var(--wr-card)',
  color: on ? 'var(--wr-on)' : 'var(--wr-ink)',
})
const field: CSSProperties = {
  font: 'inherit',
  fontSize: 14,
  padding: '7px 10px',
  borderRadius: 10,
  border: '1px solid var(--wr-line)',
  background: 'var(--wr-card)',
  color: 'var(--wr-ink)',
  width: 88,
}

const FCL_COLS: ReadonlyArray<{ id: FclBasis; label: string }> = [
  { id: '20ST', label: "20'" },
  { id: '40ST', label: "40'" },
  { id: '40HC', label: "40' HC" },
]

function FclTable({ rows }: { rows: SpotQuote[] }) {
  const offers = useMemo(() => fclOffers(rows), [rows])
  if (offers.length === 0)
    return (
      <div style={{ padding: '16px 20px', fontSize: 14, color: 'var(--wr-ink2)' }}>
        No container quotes for this lane right now.
      </div>
    )
  const low = (b: FclBasis) => {
    const v = offers.map((o) => o.prices[b]).filter((x): x is number => x != null)
    return v.length > 1 ? Math.min(...v) : null
  }
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 640 }}>
        <thead>
          <tr>
            <th style={th}>Source</th>
            {FCL_COLS.map((c) => (
              <th key={c.id} style={th}>
                {c.label}
              </th>
            ))}
            <th style={th}>Transit</th>
            <th style={th}>Departs</th>
            <th style={th}>Valid until</th>
            <th style={th}>Price covers</th>
          </tr>
        </thead>
        <tbody>
          {offers.map((o) => {
            const excl = localChargesExcluded(o.includes)
            return (
              <tr key={o.key}>
                <td style={td}>
                  <b>{o.provider}</b>
                  {o.carrier ? (
                    <div style={{ fontSize: 12, color: 'var(--wr-ink2)' }}>{o.carrier}</div>
                  ) : null}
                </td>
                {FCL_COLS.map((c) => {
                  const v = o.prices[c.id]
                  return (
                    <td
                      key={c.id}
                      style={{
                        ...td,
                        fontFamily: mono,
                        fontWeight: 600,
                        color:
                          v == null
                            ? 'var(--wr-muted)'
                            : v === low(c.id)
                              ? 'var(--wr-good)'
                              : 'var(--wr-ink)',
                      }}
                    >
                      {v == null ? '-' : fmtPrice(v)}
                    </td>
                  )
                })}
                <td style={{ ...td, fontFamily: mono, color: 'var(--wr-ink2)' }}>
                  {o.transitDays != null ? `${o.transitDays}d` : '-'}
                </td>
                <td style={{ ...td, fontFamily: mono, color: 'var(--wr-ink2)' }}>
                  {shortDate(o.departure)}
                </td>
                <td style={{ ...td, fontFamily: mono, color: 'var(--wr-ink2)' }}>
                  {shortDate(o.validUntil)}
                </td>
                <td
                  style={{ ...td, fontSize: 12, color: 'var(--wr-ink2)', maxWidth: 260 }}
                  title={o.includes ?? undefined}
                >
                  {excl === true
                    ? 'Ocean freight only; local charges extra'
                    : excl === false
                      ? 'Includes origin and destination charges'
                      : o.includes
                        ? o.includes.slice(0, 70)
                        : '-'}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

function NumberField({
  id,
  text,
  value,
  step,
  onChange,
}: { id: string; text: string; value: number; step: number; onChange: (n: number) => void }) {
  return (
    <label htmlFor={id} style={{ display: 'grid', gap: 4 }}>
      <span style={label}>{text}</span>
      <input
        id={id}
        type="number"
        min={0}
        step={step}
        value={Number.isFinite(value) ? value : ''}
        onChange={(e) => onChange(e.target.value === '' ? Number.NaN : Number(e.target.value))}
        style={field}
      />
    </label>
  )
}

function LclLadder({ rows }: { rows: SpotQuote[] }) {
  const ladder = useMemo(() => lclLadder(rows), [rows])
  const saved = useCargo()
  const [cargo, setLocal] = useState<Cargo>(saved)
  const est = useMemo(() => lclEstimate(ladder, cargo.cbm, cargo.kg), [ladder, cargo])
  const update = (c: Cargo) => {
    setLocal(c)
    if (c.cbm > 0 && c.kg >= 0) setCargo(c)
  }
  if (ladder.every((b) => !b.cheapest))
    return (
      <div style={{ padding: '16px 20px', fontSize: 14, color: 'var(--wr-ink2)' }}>
        No LCL quotes for this lane right now.
      </div>
    )
  return (
    <div>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 16,
          alignItems: 'end',
          padding: '14px 20px',
          borderBottom: '1px solid var(--wr-line)',
        }}
      >
        <NumberField
          id="wr-cbm"
          text="Your cargo, m3"
          value={cargo.cbm}
          step={0.5}
          onChange={(n) => update({ ...cargo, cbm: n })}
        />
        <NumberField
          id="wr-kg"
          text="Weight, kg"
          value={cargo.kg}
          step={50}
          onChange={(n) => update({ ...cargo, kg: n })}
        />
        <div style={{ flex: '1 1 260px', fontSize: 14 }}>
          {est.kind === 'band' && (
            <>
              <span
                style={{
                  fontFamily: mono,
                  fontSize: 22,
                  fontWeight: 700,
                  color: 'var(--wr-signal)',
                }}
              >
                from {fmtPrice(est.price)}
              </span>{' '}
              <span style={{ color: 'var(--wr-ink2)' }}>
                for up to {est.band.wm} W/M (you are {est.wm.toFixed(est.wm % 1 ? 1 : 0)} W/M)
              </span>
              {est.heavy && (
                <div style={{ fontSize: 12, color: 'var(--wr-bad)', marginTop: 4 }}>
                  Heavier than the {est.band.kgPerCbm} kg per m3 these prices assume, so the real
                  price can be higher.
                </div>
              )}
            </>
          )}
          {est.kind === 'over' && (
            <span style={{ color: 'var(--wr-ink2)' }}>
              {est.wm.toFixed(1)} W/M is above the largest size we price (10). Ask for a quote or
              ship it as a container.
            </span>
          )}
          {est.kind === 'none' && (
            <span style={{ color: 'var(--wr-ink2)' }}>
              Enter the volume and weight of your cargo.
            </span>
          )}
        </div>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 520 }}>
          <thead>
            <tr>
              <th style={th}>Size</th>
              <th style={th}>From</th>
              <th style={th}>Per W/M</th>
              <th style={th}>Offers</th>
              <th style={th}>Transit</th>
              <th style={th}>Valid until</th>
            </tr>
          </thead>
          <tbody>
            {ladder.map((b) => (
              <tr key={b.basis}>
                <td style={td}>up to {b.wm} W/M</td>
                <td style={{ ...td, fontFamily: mono, fontWeight: 700 }}>
                  {b.cheapest ? fmtPrice(b.cheapest.amount) : '-'}
                </td>
                <td style={{ ...td, fontFamily: mono, color: 'var(--wr-ink2)' }}>
                  {b.perWm != null ? fmtPrice(b.perWm) : '-'}
                </td>
                <td style={{ ...td, fontFamily: mono, color: 'var(--wr-ink2)' }}>
                  {b.offers || '-'}
                </td>
                <td style={{ ...td, fontFamily: mono, color: 'var(--wr-ink2)' }}>
                  {b.transit
                    ? b.transit[0] === b.transit[1]
                      ? `${b.transit[0]}d`
                      : `${b.transit[0]} to ${b.transit[1]}d`
                    : '-'}
                </td>
                <td style={{ ...td, fontFamily: mono, color: 'var(--wr-ink2)' }}>
                  {shortDate(b.cheapest?.valid_until)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p
        style={{
          fontSize: 12,
          color: 'var(--wr-muted)',
          margin: 0,
          padding: '10px 20px',
          lineHeight: 1.5,
        }}
      >
        LCL is priced per shipment, not per container, and the price per m3 falls steeply with size.
        1 W/M is 1 m3 or 1 tonne, whichever is greater. Prices are port to port; origin and
        destination charges are usually extra.
      </p>
    </div>
  )
}

export function SpotPanel({
  pol,
  pod,
  watch,
  tick,
}: { pol: string; pod: string; watch: WatchState | null; tick: number }) {
  const [rows, setRows] = useState<SpotQuote[] | null>(null)
  const [failed, setFailed] = useState(false)
  const [mode, setMode] = useState<'FCL' | 'LCL'>('FCL')

  // biome-ignore lint/correctness/useExhaustiveDependencies: tick is the refresh trigger; watch re-reads once the lane is registered
  useEffect(() => {
    let live = true
    setFailed(false)
    getLaneSpot(pol, pod)
      .then((r) => live && setRows(r))
      .catch(() => live && setFailed(true))
    return () => {
      live = false
    }
  }, [pol, pod, tick, watch?.state])

  const fcl = useMemo(() => (rows ?? []).filter((r) => r.mode === 'FCL'), [rows])
  const lcl = useMemo(() => (rows ?? []).filter((r) => r.mode === 'LCL'), [rows])
  const empty = rows !== null && rows.length === 0

  return (
    <div style={{ borderTop: '1px solid var(--wr-line)' }}>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 12,
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '14px 20px',
        }}
      >
        <div>
          <div style={{ fontSize: 15, fontWeight: 600 }}>Spot rates from marketplaces</div>
          <div style={{ fontSize: 12, color: 'var(--wr-ink2)', marginTop: 2 }}>
            {failed
              ? 'Could not load spot rates. Try refresh in a moment.'
              : rows === null
                ? 'Loading…'
                : empty
                  ? watch?.state === 'capped'
                    ? 'We are not adding new lanes to spot tracking right now.'
                    : 'We are fetching spot rates for this lane. New lanes appear within the hour, then refresh daily.'
                  : 'Updated daily · port to port'}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <button
            type="button"
            aria-pressed={mode === 'FCL'}
            style={tab(mode === 'FCL')}
            onClick={() => setMode('FCL')}
          >
            Container (FCL)
          </button>
          <button
            type="button"
            aria-pressed={mode === 'LCL'}
            style={tab(mode === 'LCL')}
            onClick={() => setMode('LCL')}
          >
            Part load (LCL)
          </button>
        </div>
      </div>
      {rows !== null &&
        !empty &&
        (mode === 'FCL' ? <FclTable rows={fcl} /> : <LclLadder rows={lcl} />)}
    </div>
  )
}
